<template>
  <div
    v-if="hasPopulationToPopulationSettings"
    :class="{ 'has-changes': hasChanges }"
  >
    <div class="c-population-to-population-settings-card-hub">
      <!-- Override Items -->
      <div
        v-for="(partnerPopId, index) in partnerPopulationIds"
        :key="`partner-pop-${partnerPopId}-${index}`"
      >
        <BittsDivider class="my-4" />
        <div class="flex justify-between items-center h-[48px]">
          <div class="flex items-center gap-8 text-neutral-text">
            <div class="flex items-center gap-4">
              <BittsSvg svg="cornerDownRight" class="w-16 h-16" />
              <p>Sharing</p>
            </div>

            <SharingSettingsVisibilitySelector
              placement="right"
              :read-only="true"
              :data-share="getDataShare(partnerPopId)"
              :is-own="true"
              :should-mount-to-body="false"
              display="fields"
            />
            <p>
              when overlapping {{ partnerName }}'s
              <strong>{{ getPartnerPopulationName(partnerPopId) }}</strong>
            </p>
          </div>

          <BittsTooltip v-if="hasSharingPermission" :mount-to-body="true">
            <BittsButton
              @click="handleDeletePopulationToPopulationSetting(partnerPopId)"
              variant="outline"
              type="neutral"
              size="x-small"
              class="p-6"
            >
              <template #icon>
                <FontAwesomeIcon
                  :icon="['fak', 'delete']"
                  :style="{
                    height: '12px',
                    width: '12px',
                    color: 'currentColor',
                  }"
                  class="text-neutral-text-weak"
                />
              </template>
            </BittsButton>
            <template #title>Remove override</template>
          </BittsTooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsDivider,
  BittsSvg,
  BittsTooltip,
} from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, ref, watchEffect } from 'vue';

import { PopulationToPopulationMap } from '@/components/partners/DataSharingSettings/SettingsModal/SharingSettingsTab/composables/useSharingSettings';
import SharingSettingsVisibilitySelector from '@/components/sharing-settings/SharingSettingsVisibilitySelector.vue';

import useAuth from '@/composables/useAuth';
import usePopulations from '@/composables/usePopulations';

const props = defineProps<{
  populationId: number;
  partnerName: string;
  partnerId: number;
  deleteOverride: (
    shareId: number,
    partnerPopulationId: number,
    populationId: number,
  ) => void;
  currentPopulationToPopulation: PopulationToPopulationMap;
  hasChanges?: boolean;
}>();

interface PopulationToPopulationSetting {
  partnerPopulation: {
    id: number;
    name: string;
  };
  dataShare: {
    id: number;
  };
}

const { hasPermission } = useAuth();
const hasSharingPermission = computed(() => hasPermission('manage:sharing'));

const { customPopsWithStatsAndSharing, allPopulations } = usePopulations(
  props.partnerId as never,
);

const population = computed(() => {
  return [...allPopulations.value, ...customPopsWithStatsAndSharing.value].find(
    (pop) => pop.id === props.populationId,
  );
});

const populationToPopulationSettings = computed(() => {
  if (!population.value?.populationToPopulationSettings) return [];
  return population.value
    .populationToPopulationSettings as PopulationToPopulationSetting[];
});

const localPartnerPopIds = ref<number[]>([]);

watchEffect(() => {
  const popToPopSettings =
    props.currentPopulationToPopulation[props.populationId];
  if (!popToPopSettings) {
    localPartnerPopIds.value = [];
  } else {
    localPartnerPopIds.value = Object.keys(popToPopSettings).map(Number);
  }
});

const partnerPopulationIds = computed(() => localPartnerPopIds.value);

const hasPopulationToPopulationSettings = computed(() => {
  return partnerPopulationIds.value.length > 0;
});

function getDataShare(partnerPopId: number) {
  const setting = populationToPopulationSettings.value.find(
    (s) => s.partnerPopulation?.id === partnerPopId,
  );
  return setting?.dataShare || {};
}

function getPartnerPopulationName(partnerPopId: number): string {
  const setting = populationToPopulationSettings.value.find(
    (s) => s.partnerPopulation?.id === partnerPopId,
  );
  return setting?.partnerPopulation?.name || 'Unknown Population';
}

function handleDeletePopulationToPopulationSetting(partnerPopId: number) {
  const setting = populationToPopulationSettings.value.find(
    (s) => s.partnerPopulation?.id === partnerPopId,
  );
  if (setting?.dataShare?.id) {
    props.deleteOverride(
      setting.dataShare.id,
      partnerPopId,
      props.populationId,
    );
  }
}
</script>
<style scoped lang="pcss">
@reference "../../../../../../css/theme.css";
.c-population-to-population-settings-card-hub {
  @apply pl-48 pr-16 py-4 relative;
}

/* Apply same background color as the row when it has changes */
.has-changes {
  background-color: rgba(221, 222, 255, 0.25);
}
</style>
